import React from 'react';
import { Insight } from '@/api/insightsApi';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { chartColors } from './chartColors';
import { CustomTooltip } from './CustomTooltip';
import { useAppTheme } from '@/contexts/AppThemeContext';
import { useRechartsTheme } from '@/hooks/useRechartsTheme';

interface OpenEndsVisualizationProps {
  insight: Insight;
}

export const OpenEndsVisualization: React.FC<OpenEndsVisualizationProps> = ({ insight }) => {
  const { theme } = useAppTheme();
  const chartTheme = useRechartsTheme();
  const activitiesAffected = insight.activitiesAffected || 0;
  
  // Parse description to extract missing predecessors/successors
  const desc = insight.description || '';
  const noPredMatch = desc.match(/(\d+)\s+missing predecessors/);
  const noSuccMatch = desc.match(/(\d+)\s+missing successors/);
  const noPred = noPredMatch ? Number.parseInt(noPredMatch[1], 10) : Math.floor(activitiesAffected * 0.6);
  const noSucc = noSuccMatch ? Number.parseInt(noSuccMatch[1], 10) : Math.floor(activitiesAffected * 0.4);

  const openEndsData = [
    { name: 'Missing Predecessors', value: noPred, color: chartColors.warning },
    { name: 'Missing Successors', value: noSucc, color: chartColors.primary },
  ];

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-4">
        <div className={`rounded-lg p-4 border ${
          theme === 'dark' ? 'bg-gray-800/80 border-gray-700' : 'bg-white border-gray-200'
        }`}>
          <div className={`text-xs mb-1 ${
            theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
          }`}>Total Open Ends</div>
          <div className="text-2xl font-bold text-amber-400">{activitiesAffected}</div>
        </div>
        <div className={`rounded-lg p-4 border ${
          theme === 'dark' ? 'bg-gray-800/80 border-gray-700' : 'bg-white border-gray-200'
        }`}>
          <div className={`text-xs mb-1 ${
            theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
          }`}>Missing Predecessors</div>
          <div className="text-2xl font-bold text-yellow-400">{noPred}</div>
        </div>
        <div className={`rounded-lg p-4 border ${
          theme === 'dark' ? 'bg-gray-800/80 border-gray-700' : 'bg-white border-gray-200'
        }`}>
          <div className={`text-xs mb-1 ${
            theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
          }`}>Missing Successors</div>
          <div className="text-2xl font-bold text-yellow-400">{noSucc}</div>
        </div>
      </div>

      {/* Open Ends Breakdown */}
      <div className={`rounded-lg p-4 border ${
        theme === 'dark' ? 'bg-gray-800/80 border-gray-700' : 'bg-white border-gray-200'
      }`}>
        <h4 className={`text-sm font-semibold mb-4 ${
          theme === 'dark' ? 'text-white' : 'text-gray-900'
        }`}>Open Ends Breakdown</h4>
        <ResponsiveContainer width="100%" height={280}>
          <PieChart margin={{ top: 10, right: 10, bottom: 10, left: 10 }}>
            <Pie
              data={openEndsData}
              cx="50%"
              cy="45%"
              labelLine={false}
              label={false}
              outerRadius={70}
              fill="#8884d8"
              dataKey="value"
            >
              {openEndsData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip 
              content={<CustomTooltip formatter={(value: number, name?: string) => [
                `${value} activities`,
                name || 'Count'
              ]} />}
            />
            <Legend 
              verticalAlign="bottom"
              height={60}
              wrapperStyle={{ 
                color: chartTheme.textColor,
                paddingTop: '10px',
                fontSize: '12px'
              }}
              iconType="circle"
              formatter={(value: string) => (
                <span style={{ color: chartTheme.textColor }}>
                  {value}: {openEndsData.find(d => d.name === value)?.value || 0}
                </span>
              )}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};



